import { TEE_SHEET_CONFIG } from "./config"

const questionHasAnswer = (question) => {
	const value = question?.answer
	if (Array.isArray(value)) return value.length > 0
	return value !== null && value !== undefined && value !== ""
}

const findSavedQuestion = (savedSection, questionId) =>
	(savedSection?.questions || []).find((question) => question.id === questionId)

const findQuestionAnswer = (snapshot, questionId) => {
	for (const section of snapshot?.sections || []) {
		const question = (section.questions || []).find((q) => q.id === questionId)
		if (question) return question.answer
	}
	return null
}

const defaultSectionEnabled = (section) => !section.triggerQuestionId

const compactObject = (object) =>
	Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined))

export const buildTeeSheetSnapshot = (config = TEE_SHEET_CONFIG) => ({
	config_version: config.version,
	additional_notes: "",
	sections: (config.sections || []).map((section) => ({
		id: section.id,
		title: section.title,
		description: section.description || "",
		triggerQuestionId: section.triggerQuestionId || null,
		enabled: defaultSectionEnabled(section),
		questions: (section.questions || []).map((question) => ({
			id: question.id,
			question: question.question,
			helpText: question.helpText || "",
			type: question.type || "text",
			options: question.options || null,
			required: question.required === true,
			answer: null,
		})),
	})),
})

export const mergeTeeSheetSnapshot = (existing, config = TEE_SHEET_CONFIG) => {
	const savedSections = existing?.sections || []

	const sections = (config.sections || []).map((section) => {
		const savedSection = savedSections.find((s) => s.id === section.id)
		const configQuestionIds = new Set((section.questions || []).map((q) => q.id))

		const questions = (section.questions || []).map((question) => {
			const savedQuestion = findSavedQuestion(savedSection, question.id)
			return {
				id: question.id,
				question: savedQuestion?.question || question.question,
				helpText: savedQuestion?.helpText || question.helpText || "",
				type: savedQuestion?.type || question.type || "text",
				options: question.options || savedQuestion?.options || null,
				required: question.required === true,
				dependsOn: question.dependsOn || null,
				disabledWhen: question.disabledWhen || null,
				prefix: question.prefix || null,
				contactFields: question.contactFields || null,
				labels: question.labels || null,
				answer: savedQuestion && "answer" in savedQuestion ? savedQuestion.answer : null,
				answered_at: savedQuestion?.answered_at || null,
				answered_by: savedQuestion?.answered_by || null,
			}
		})

		for (const savedQuestion of savedSection?.questions || []) {
			if (configQuestionIds.has(savedQuestion.id)) continue
			questions.push({
				...savedQuestion,
				historical: true,
				readOnly: true,
			})
		}

		return {
			id: section.id,
			title: section.title,
			description: section.description || savedSection?.description || "",
			triggerQuestionId: section.triggerQuestionId || null,
			enabled:
				typeof savedSection?.enabled === "boolean"
					? savedSection.enabled
					: defaultSectionEnabled(section),
			questions,
		}
	})

	for (const savedSection of savedSections) {
		if (sections.some((section) => section.id === savedSection.id)) continue
		sections.push({
			...savedSection,
			historical: true,
			readOnly: true,
			questions: (savedSection.questions || []).map((question) => ({
				...question,
				historical: true,
				readOnly: true,
			})),
		})
	}

	return {
		config_version: config.version,
		additional_notes: existing?.additional_notes || "",
		updated_at: existing?.updated_at || null,
		updated_by: existing?.updated_by || null,
		sections,
	}
}

export const getEnabledSectionIds = (snapshot) =>
	(snapshot?.sections || [])
		.filter((section) => {
			if (!section.triggerQuestionId) return section.enabled !== false
			return section.enabled === true || findQuestionAnswer(snapshot, section.triggerQuestionId) === true
		})
		.map((section) => section.id)

export const countAnsweredQuestions = (snapshot) => {
	const enabledIds = new Set(getEnabledSectionIds(snapshot))
	let answered = 0
	let total = 0

	for (const section of snapshot?.sections || []) {
		if (!enabledIds.has(section.id)) continue
		for (const question of section.questions || []) {
			if (question.historical) continue
			total += 1
			if (questionHasAnswer(question)) answered += 1
		}
	}

	return { answered, total }
}

export const updateQuestionAnswer = (snapshot, sectionId, questionId, answer, userId = null) => ({
	...snapshot,
	sections: (snapshot.sections || []).map((section) => {
		if (section.id !== sectionId) return section
		return {
			...section,
			questions: (section.questions || []).map((question) => {
				if (question.id !== questionId || question.readOnly) return question
				return {
					...question,
					answer,
					answered_by: userId,
					answered_at: new Date().toISOString(),
				}
			}),
		}
	}),
})

export const setSectionEnabled = (snapshot, sectionId, enabled) => ({
	...snapshot,
	sections: (snapshot.sections || []).map((section) =>
		section.id === sectionId ? { ...section, enabled } : section
	),
})

export const prepareTeeSheetForSave = (snapshot, userId, updatedAt) => ({
	config_version: snapshot.config_version,
	additional_notes: snapshot.additional_notes || "",
	updated_at: updatedAt,
	updated_by: userId || null,
	sections: (snapshot.sections || []).map((section) =>
		compactObject({
			id: section.id,
			title: section.title,
			description: section.description,
			triggerQuestionId: section.triggerQuestionId,
			enabled: section.enabled !== false,
			historical: section.historical || undefined,
			readOnly: section.readOnly || undefined,
			questions: (section.questions || []).map((question) =>
				compactObject({
					id: question.id,
					question: question.question,
					helpText: question.helpText,
					type: question.type,
					options: question.options,
					required: question.required || undefined,
					answer: question.answer === undefined ? null : question.answer,
					answered_at: question.answered_at,
					answered_by: question.answered_by,
					historical: question.historical || undefined,
					readOnly: question.readOnly || undefined,
				})
			),
		})
	),
})
